import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { UserCog, ArrowRight, Loader2, ShieldCheck, HandCoins, Rocket, TrendingUp } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../lib/AuthContext";
import { Button, TextArea } from "../../components/common/Primitives";

const ROLE_META = {
  startup: { icon: Rocket, label: "Founder", sub: "Raise equity from vetted investors", to: "/startup/dashboard" },
  investor: { icon: TrendingUp, label: "Investor", sub: "Access curated private-market deals", to: "/investor/dashboard" },
};

/**
 * /settings/role-change-request?to=investor
 *
 * The Google OAuth "you're already registered as X" popup lands users
 * here. We create a `profile_change_requests` doc with field="role" so
 * the existing admin queue (`/admin/profile-requests`) can approve it
 * without a new surface.
 */
export default function RoleChangeRequest() {
  const { user } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const target = new URLSearchParams(loc.search).get("to");
  const [reason, setReason] = useState("");
  const [pending, setPending] = useState(null); // existing request
  const [submitting, setSubmitting] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const currentRole = (user?.role || "").toLowerCase();
  const canSwitch = useMemo(() => new Set(["startup", "investor"]), []);
  const invalid = !user || !canSwitch.has(currentRole) || !target || !canSwitch.has(target) || target === currentRole;

  useEffect(() => {
    if (!user) return;
    api.get("/profile/me/change-requests")
      .then(({ data }) => {
        const rows = data?.items || [];
        const p = rows.find((r) => r.field === "role" && r.status === "Pending");
        setPending(p || null);
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [user]);

  const submit = async () => {
    if (invalid) return;
    setSubmitting(true);
    try {
      await api.post("/profile/me/change-request", {
        field: "role",
        new_value: target,
        reason: reason.trim() || `Requesting switch from ${currentRole} to ${target}.`,
      });
      toast.success("Role change requested. Admin will review shortly.");
      // Refetch so the "already pending" state renders
      const r = await api.get("/profile/me/change-requests");
      const p = (r.data?.items || []).find((x) => x.field === "role" && x.status === "Pending");
      setPending(p || null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const cancel = async () => {
    if (!pending) return;
    if (!window.confirm("Cancel this pending role change request?")) return;
    try {
      await api.delete(`/profile/me/change-request/${pending.request_id}`);
      toast.success("Request cancelled.");
      setPending(null);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="glass rounded-lg p-8 max-w-md w-full text-center">
          <Loader2 size={36} className="animate-spin text-navy mx-auto mb-3" />
          <div className="text-sm text-ink-muted">Loading…</div>
        </div>
      </div>
    );
  }

  const CurrentIcon = (ROLE_META[currentRole]?.icon) || UserCog;
  const TargetIcon = (target && ROLE_META[target]?.icon) || HandCoins;
  const curMeta = ROLE_META[currentRole] || { label: currentRole, sub: "" };
  const tgtMeta = ROLE_META[target] || { label: target || "—", sub: "" };

  return (
    <div className="min-h-screen bg-cream/60 py-10 px-6" data-testid="role-change-request-page">
      <Toaster richColors position="top-right" />
      <div className="max-w-2xl mx-auto">
        <Link to="/settings/profile" className="text-xs font-mono uppercase tracking-widest text-ink-muted hover:text-ink" data-testid="role-change-back">
          ← Back to profile
        </Link>
        <div className="mt-4 glass rounded-lg p-8">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-12 h-12 rounded-full inline-flex items-center justify-center bg-gold/20 text-[#8A6600] shrink-0">
              <UserCog size={22} />
            </div>
            <div>
              <div className="overline mb-1">Account · Role Change</div>
              <h1 className="font-heading text-2xl text-ink leading-tight" data-testid="role-change-heading">
                Request a role change
              </h1>
              <p className="text-sm text-ink-muted mt-1">
                We keep every account tied to a single primary surface. Ops will review and switch you within a business day.
              </p>
            </div>
          </div>

          {invalid && (
            <div className="border border-amber-300 bg-amber-50 rounded-md p-4 text-sm text-amber-900" data-testid="role-change-invalid-target">
              {!target || !canSwitch.has(target)
                ? "This page needs a valid target role. Try clicking the link from the Google sign-in popup again."
                : target === currentRole
                  ? "You already have this role — no request needed."
                  : `Self-service role change isn't available for ${currentRole}. Please contact ops directly.`}
              <div className="mt-3">
                <Link to="/support" className="inline-flex items-center gap-1 text-navy hover:underline text-xs font-heading">
                  Contact support <ArrowRight size={12} />
                </Link>
              </div>
            </div>
          )}

          {!invalid && loaded && (
            <>
              {/* From → To visual */}
              <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 items-stretch mb-5">
                <div className="border border-white/20 bg-white/70 backdrop-blur rounded-lg p-4" data-testid="role-change-from">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-2">Currently</div>
                  <div className="flex items-start gap-3">
                    <CurrentIcon size={20} className="text-ink-soft mt-0.5" />
                    <div>
                      <div className="font-heading text-ink">{curMeta.label}</div>
                      <div className="text-xs text-ink-muted">{curMeta.sub || ""}</div>
                    </div>
                  </div>
                </div>
                <div className="hidden md:flex items-center justify-center text-[#B8860B]">
                  <ArrowRight size={18} />
                </div>
                <div className="border border-gold/40 bg-gold/[0.08] rounded-lg p-4" data-testid="role-change-to">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#8A6600] mb-2">Requested</div>
                  <div className="flex items-start gap-3">
                    <TargetIcon size={20} className="text-[#8A6600] mt-0.5" />
                    <div>
                      <div className="font-heading text-ink">{tgtMeta.label}</div>
                      <div className="text-xs text-ink-muted">{tgtMeta.sub || ""}</div>
                    </div>
                  </div>
                </div>
              </div>

              {pending ? (
                <div className="border border-emerald-300 bg-emerald-50 rounded-md p-4" data-testid="role-change-pending">
                  <div className="flex items-start gap-2">
                    <ShieldCheck size={18} className="text-emerald-700 mt-0.5" />
                    <div className="flex-1">
                      <div className="text-sm text-emerald-900 font-heading">Request pending admin review</div>
                      <div className="text-xs text-emerald-800/80 mt-1">
                        Submitted {new Date(pending.created_at).toLocaleString("en-IN")} · target: <b>{pending.new_value}</b>
                      </div>
                      {pending.reason && <div className="text-xs text-emerald-900/80 mt-1 italic">“{pending.reason}”</div>}
                    </div>
                    <button
                      onClick={cancel}
                      className="text-xs font-heading text-emerald-800 hover:text-rose-700 underline underline-offset-4"
                      data-testid="role-change-cancel-btn"
                    >Cancel</button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="mb-4">
                    <TextArea
                      label="Why do you need this change? (optional but helps ops)"
                      placeholder={`E.g. "I'm the founder of X — I already invest personally too; please move me to investor."`}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      rows={4}
                      testId="role-change-reason"
                    />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button onClick={submit} disabled={submitting} data-testid="role-change-submit">
                      {submitting ? "Submitting…" : "Submit request"}
                    </Button>
                    <button
                      type="button"
                      onClick={() => nav(curMeta.to || "/")}
                      className="px-4 py-2 text-sm font-heading text-ink-soft hover:text-ink"
                      data-testid="role-change-abort"
                    >
                      Continue as {curMeta.label}
                    </button>
                  </div>
                </>
              )}

              <div className="mt-6 text-[11px] text-ink-muted leading-relaxed">
                <b>Note:</b> Approving a role change keeps your existing profile intact. A fresh {tgtMeta.label} shell is created if you don&apos;t already have one, and your current KYC status carries over.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
